/**
 * Emergency phone number lookup by country/destination.
 * Extracted from WanderAppComplete.jsx for shared use by web and mobile.
 *
 * Maps country/region names to their emergency numbers. Used in
 * the safety info section of generated itineraries.
 */

/**
 * Map of country/region names to emergency phone numbers.
 * Keys are lowercase for case-insensitive matching.
 * Some entries include multiple numbers for different services.
 */
export const EMERGENCY_NUMBERS = {
  // North America
  'united states': '911', 'usa': '911', 'us': '911', 'canada': '911', 'mexico': '911',

  // UK & Ireland
  'united kingdom': '999', 'uk': '999', 'england': '999', 'scotland': '999', 'wales': '999',

  // EU & 112 countries
  'european union': '112', 'eu': '112',
  'france': '112', 'germany': '112', 'italy': '112', 'spain': '112', 'portugal': '112',
  'netherlands': '112', 'belgium': '112', 'austria': '112', 'greece': '112', 'ireland': '112',
  'sweden': '112', 'norway': '112', 'denmark': '112', 'finland': '112', 'iceland': '112',
  'switzerland': '112', 'czech republic': '112', 'poland': '112', 'hungary': '112',
  'croatia': '112', 'romania': '112', 'bulgaria': '112', 'slovakia': '112', 'slovenia': '112',
  'turkey': '112',

  // North Africa & Middle East
  'morocco': '15 (ambulance) / 19 (police)', 'egypt': '122',
  'uae': '999', 'dubai': '999', 'qatar': '999', 'saudi arabia': '911',
  'israel': '100 (police) / 101 (ambulance)', 'jordan': '911',

  // East Asia
  'japan': '110 (police) / 119 (fire/ambulance)', 'south korea': '112',
  'china': '110 (police) / 120 (ambulance)',
  'taiwan': '110', 'hong kong': '999', 'macau': '999',

  // South & Southeast Asia
  'india': '112', 'thailand': '191',
  'indonesia': '112', 'bali': '112', 'vietnam': '113', 'cambodia': '117',
  'singapore': '999', 'malaysia': '999', 'philippines': '911',
  'nepal': '100', 'bhutan': '113', 'mongolia': '102',
  'sri lanka': '119', 'maldives': '119',

  // Oceania
  'australia': '000', 'new zealand': '111',

  // Sub-Saharan Africa
  'south africa': '10111', 'kenya': '999', 'tanzania': '112', 'ethiopia': '991',

  // South America
  'brazil': '190', 'argentina': '911', 'colombia': '123', 'peru': '105', 'chile': '131',

  // Central America & Caribbean
  'costa rica': '911', 'panama': '911',
  'cuba': '106', 'jamaica': '119', 'bahamas': '919',

  // Pacific Islands
  'french polynesia': '15 (ambulance) / 17 (police)', 'bora bora': '15 (ambulance) / 17 (police)',
  'fiji': '911',

  // Other
  'russia': '112', 'georgia': '112', 'armenia': '911',
  'greenland': '112', 'faroe islands': '112',
};

/**
 * Look up the emergency number for a destination.
 * Tries exact match first, then partial match against known country names.
 *
 * @param {string} destinationName - Destination name (city, country, or region)
 * @returns {string|null} Emergency number(s) or null if unknown (falls back to AI result)
 */
export function getEmergencyNumber(destinationName) {
  if (!destinationName) return null;
  const lower = destinationName.toLowerCase();
  // Try exact match first, then partial match
  for (const [key, number] of Object.entries(EMERGENCY_NUMBERS)) {
    if (lower.includes(key) || key.includes(lower.split(',')[0].trim())) {
      return number;
    }
  }
  return null; // Unknown — will fall back to AI result
}
